import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";
import Select from "react-select";
import { allCountries } from "country-telephone-data";

const getFlagEmoji = (countryCode) =>
  countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const countryOptions = allCountries.map((country) => ({
  label: `${getFlagEmoji(country.iso2)} ${country.name} (+${country.dialCode})`,
  value: `+${country.dialCode}`,
}));

const getInitialForm = () => ({
  full_name: "",
  email: "",
  phone: "",
  ticket_type: "",
  is_send_by_email: true,
  note: "",
  conditional_entry_enabled: false,
  access_from: "",
  access_until: "",
});

const InvitationsFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  eventId,
}) => {
  const [form, setForm] = useState(getInitialForm());

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePhoneChange = (value) => {
    let cleaned = value.replace(/\D/g, "");

    handleChange("phone", cleaned);
  };

  const handleSubmit = () => {
    onSubmit({
      ...form,
      event: eventId,
      access_from: form.access_from ? `${form.access_from}:00` : null,
      access_until: form.access_until ? `${form.access_until}:00` : null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            Generate Invitation
          </h2>

          <button
            onClick={() => {
              setForm(getInitialForm());
              onClose();
            }}
            className="text-slate-400 hover:text-white"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Name + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Full Name
              </label>
              <input
                value={form.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Phone</label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {/* Country Code Dropdown */}
              <div className="w-full sm:w-40">
                <Select
                  options={countryOptions}
                  value={countryOptions.find(
                    (opt) => opt.value === form.countryCode,
                  )}
                  placeholder="Country Code"
                  onChange={(selected) =>
                    handleChange("countryCode", selected.value)
                  }
                  className="text-sm"
                  styles={customSelectStyles}
                />
              </div>

              {/* Phone Input */}
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="Enter phone number"
                className="flex-1 bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          {/* Ticket Type */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Ticket Type
            </label>
            <select
              value={form.ticket_type}
              onChange={(e) => handleChange("ticket_type", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            >
              <option value="">Select type</option>
              <option value="general_access">General Access</option>
              <option value="vip">VIP</option>
              <option value="backstage">Backstage</option>
              <option value="backstage_friends_family">
                Backstage - Friends & Family
              </option>
              <option value="aaa_artist">AAA - Artist</option>
              <option value="aaa_technical">AAA - Technical</option>
              <option value="aaa_bar_staff">AAA - Bar Staff</option>
              <option value="aaa_other_management">
                AAA - Other Management
              </option>
              <option value="aaa_third_party">AAA - Third Party</option>
              <option value="aaa_friends_family">AAA - Friends & Family</option>
              <option value="dj_booth">DJ Booth</option>
            </select>
          </div>

          {/* Send by email */}
          <div>
            <label className="text-sm text-slate-300 mb-1">
              Send by email:
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={form.is_send_by_email === true}
                  onChange={() => handleChange("is_send_by_email", true)}
                  className="accent-pink-600"
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={form.is_send_by_email === false}
                  onChange={() => handleChange("is_send_by_email", false)}
                  className="accent-pink-600"
                />
                No
              </label>
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">Note</label>
            <textarea
              value={form.note}
              onChange={(e) => handleChange("note", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white min-h-[80px]"
            />
          </div>

          {/* Conditional Entry */}
          <div>
            <label className="text-sm text-slate-300 mb-1">
              Conditional Entry Enabled:
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={form.conditional_entry_enabled === true}
                  onChange={() =>
                    handleChange("conditional_entry_enabled", true)
                  }
                  className="accent-pink-600"
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={form.conditional_entry_enabled === false}
                  onChange={() =>
                    handleChange("conditional_entry_enabled", false)
                  }
                  className="accent-pink-600"
                />
                No
              </label>
            </div>
          </div>

          {/* Conditional Dates */}
          {form.conditional_entry_enabled && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-300 mb-1">
                  Access From
                </label>
                <input
                  type="datetime-local"
                  value={form.access_from}
                  onChange={(e) => handleChange("access_from", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
              </div>

              <div>
                <label className="text-sm text-slate-300 mb-1">
                  Access Until
                </label>
                <input
                  type="datetime-local"
                  value={form.access_until}
                  onChange={(e) => handleChange("access_until", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={() => {
              setForm(getInitialForm());
              onClose();
            }}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition
              ${
                loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }
            `}
          >
            {loading ? "Saving..." : "Send Invitation"}
          </button>
        </div>
      </div>
    </div>
  );
};

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#131C31",
    borderColor: state.isFocused
      ? "rgba(236, 72, 153, 0.6)" // pink focus
      : "rgba(255,255,255,0.1)",
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(236, 72, 153, 0.6)",
    },
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: "#131C31",
    border: "1px solid rgba(255,255,255,0.1)",
    zIndex: 50,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "rgba(236, 72, 153, 0.2)"
      : state.isSelected
        ? "rgba(236, 72, 153, 0.4)"
        : "transparent",
    color: "#fff",
    cursor: "pointer",
  }),

  singleValue: (base) => ({
    ...base,
    color: "#fff",
  }),

  input: (base) => ({
    ...base,
    color: "#fff",
  }),

  placeholder: (base) => ({
    ...base,
    color: "#94a3b8", // slate-400
  }),

  dropdownIndicator: (base) => ({
    ...base,
    color: "#94a3b8",
    "&:hover": {
      color: "#fff",
    },
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),
};

export default InvitationsFormModal;
